import { getToken } from "../../../../shared/data/storage/authStorage";
import { ListParams } from "../../domain/entities/CreditSummaryEntity";
import { API_BASE_URL } from "../../../../config/api";

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export class RecoApiDataSource {
  private getAuthHeaders() {
    const token = getToken();
    if (!token) {
      throw new APIError("No authentication token found", 401);
    }
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        // Convert camelCase to snake_case for backend compatibility
        const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
        searchParams.append(snakeKey, String(value));
      }
    });
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
  }

  // Customer Credits
  async fetchCustomerCreditsSummary(autoRefresh?: boolean) {
    try {
      const params = autoRefresh ? { auto_refresh: "true" } : {};
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/invoices/customer-credits-summary${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch customer credits summary",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async fetchCustomerCreditsList(params: ListParams) {
    try {
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/invoices/customer-credits-list${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch customer credits list",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async fetchCustomerOutstandingInvoices(params: ListParams) {
    try {
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/invoices/customer-outstanding-invoices${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch customer outstanding invoices",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async triggerCustomerCreditsRefresh() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/invoices/refresh-customer-credits-cache`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to refresh customer credits cache",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  // Vendor Credits
  async fetchVendorCreditsSummary(autoRefresh?: boolean) {
    try {
      const params = autoRefresh ? { auto_refresh: "true" } : {};
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/bills/vendor-credits-summary${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch vendor credits summary",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async fetchVendorCreditsList(params: ListParams) {
    try {
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/bills/vendor-credits-list${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch vendor credits list",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async fetchVendorOutstandingBills(params: ListParams) {
    try {
      const queryString = this.buildQueryString(params);

      const response = await fetch(
        `${API_BASE_URL}/api/bills/vendor-outstanding-bills${queryString}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch vendor outstanding bills",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  async triggerVendorCreditsRefresh() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bills/refresh-vendor-credits-cache`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to refresh vendor credits cache",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  // Bank Payments
  async fetchBankPaymentsSummary() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/cashAndBank/pending-summary`,
        {
          method: "GET",
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to fetch bank payments summary",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  // Apply Credits to Invoices
  async applyCreditsToInvoices(
    customerId: string,
    invoices: Array<{
      invoice_id: string;
      amount: number;
      tds_tax_amount?: number;
      tds_tax_id?: string;
      tds_section?: string;
      tds_tax_percentage?: number;
      tds_tax_name?: string;
      is_tds_applied: number;
    }>
  ) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/invoices/apply-credits`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            customer_id: customerId,
            invoices: invoices,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to apply credits to invoices",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }

  // Apply Credits to Bills
  async applyCreditsToBills(
    vendorId: string,
    bills: Array<{
      bill_id: string;
      bill_number: string;
      amount: number;
      balance: number;
    }>
  ) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bills/apply-credits`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          vendor_id: vendorId,
          bills: bills,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new APIError(
          errorData.message || "Failed to apply credits to bills",
          response.status,
          errorData
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof APIError) throw error;
      throw new APIError(
        `Network error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        500
      );
    }
  }
}

// Export standalone function for convenience
export async function applyCreditsToInvoices(
  customerId: string,
  invoices: Array<{
    invoice_id: string;
    amount: number;
    tds_tax_amount?: number;
    tds_tax_id?: string;
    tds_section?: string;
    tds_tax_percentage?: number;
    tds_tax_name?: string;
    is_tds_applied: number;
  }>
) {
  const dataSource = new RecoApiDataSource();
  return await dataSource.applyCreditsToInvoices(customerId, invoices);
}

export async function applyCreditsToBills(
  vendorId: string,
  bills: Array<{
    bill_id: string;
    bill_number: string;
    amount: number;
    balance: number;
  }>
) {
  const dataSource = new RecoApiDataSource();
  return await dataSource.applyCreditsToBills(vendorId, bills);
}
